<script lang="ts">
	//Hooks
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		localGroupRequest,
		cloudGroupRequestV2,
		handleCloudLocalLists,
		calculateTempRange,
		getDevicesFormatted,
	} from './control_group_helpers';
	import { TempButtonsTypes } from 'src/helpers/enums';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	//Stores
	import {
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	//Design system components
	import {
		Button,
		ButtonGroup,
		ControlsContainer,
		TemperatureWheel,
		toast,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	import { get_local_status } from 'src/services/local_requests';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let white_mode_lights = [];

	$: memberStatusStore = createMemberStatusSnapshot(white_mode_lights.map((d) => d.id));
	$: memberStatus = $memberStatusStore;

	let cloud = {} as Device,
		local = {} as Device,
		valueRange = [3000, 6500],
		temp = 3000,
		loading = false;

	$: tempButtonsValues = {
		[TempButtonsTypes.WARM]: valueRange[0],
		[TempButtonsTypes.WHITE]: (valueRange[0] + valueRange[1]) / 2,
		[TempButtonsTypes.COLD]: valueRange[1],
	};

	const colors = {
		[TempButtonsTypes.WARM]: 'var(--color-warning)',
		[TempButtonsTypes.WHITE]: 'var(--color-white)',
		[TempButtonsTypes.COLD]: 'var(--color-secondary)',
	};

	beforeUpdate(() => {
		//we must have only online devices, but because of DUO I'm not sure, so, before check it, find device for sure
		let device = white_mode_lights.find((d) => memberStatus[real_id(d.id)] !== undefined);
		temp = is_gen_2(device)
			? memberStatus[real_id(device?.id)]?.[get_gen_2_component_channel_structure(device)]?.ct
			: memberStatus[real_id(device?.id)]?.lights?.[0]?.temp || 3000;

		valueRange = calculateTempRange(white_mode_lights);

		if (temp < valueRange[0]) {
			temp = valueRange[0];
		} else if (temp > valueRange[1]) {
			temp = valueRange[1];
		}

		const lists = handleCloudLocalLists(
			Array.from(Object.values(white_mode_lights).map((d: Device) => d.id)),
		);
		[cloud, local] = [lists.cloud, lists.local];
	});

	const sendRequest = async (value) => {
		temp = value;
		loading = true;
		const data = { temperature: value || 0 };

		if (Object.keys(cloud).length > 0) {
			let tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			await cloudGroupRequestV2(`/v2/devices/set/groups`, tmpDevices, data);
		}

		if (Object.keys(local).length > 0) {
			for (const id in local) {
				let res;
				if (is_gen_2(local[id])) {
					data['ct'] = value || 0;
					delete data.temperature;
					res = await localGroupRequest(
						local[id],
						data,
						`${get_gen_2_component_from_category($devices[id])}.set`,
					);
				} else {
					res = await localGroupRequest(local[id], data, 'post');
				}
				if (res.isok) {
					const status_req = await get_local_status($devices[id], $devices[id].ip);
					let newStatus = {
						...memberStatus[real_id(id)],
						...status_req,
					};
					devices_status.update_device_status({
						id: $devices[id].id,
						msg: newStatus,
					});
					await update_settings($devices[id]);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
		}
		loading = false;
	};

	const ariaLabel = $format('label_color_temperature', { default: 'Color temperature' });
	const ariaLabelValue = $format('label_color_temperature_value', {
		values: { value: temp },
		default: 'Color temperature value is ${value}',
	});
	const tempWheelAriaLabel = $format('label_color_temperature', { default: 'Color temperature' });
	const thumbAriaLabel = $format('label_color_temperature_value', {
		values: { value: temp },
		default: 'Color temperature value is ${value}',
	});
	const decrementBtnAriaLabel = $format('label_color_temperature', { default: 'Color temperature' });
	const incrementBtnAriaLabel = $format('label_color_temperature_value', {
		values: { value: temp },
		default: 'Color temperature value is ${value}',
	});

	const a11yProps = {
		ariaLabel,
		ariaLabelValue,
		tempWheelAriaLabel,
		thumbAriaLabel,
		decrementBtnAriaLabel,
		incrementBtnAriaLabel,
	};
</script>

<TemperatureWheel
	{a11yProps}
	icon="fa-light fa-sun-bright"
	class="mb-l"
	value={temp}
	min={valueRange[0]}
	max={valueRange[1]}
	disabled={loading}
	on:value_change={async (e) => await sendRequest(e.detail.value)}
	isLoading={loading}
/>

<ControlsContainer class="mb-xl" color={$showNewLayout ? 'color-0' : undefined}>
	<ButtonGroup
		id="buttongroup-yahwqj2"
		slot="first"
		aria-label={$format('color_temperature_buttons', {
			default: 'Color temperature buttons',
		})}
	>
		{#each Object.keys(TempButtonsTypes) as type}
			<Button
				class={temp === tempButtonsValues[TempButtonsTypes[type]] ? 'active' : ''}
				size="m"
				variant="custom"
				customSettings={{
					background: colors[TempButtonsTypes[type]],
				}}
				on:click={async () => await sendRequest(tempButtonsValues[TempButtonsTypes[type]])}
				>{$format(`button_${TempButtonsTypes[type]}`).toUpperCase()}</Button
			>
		{/each}
	</ButtonGroup>
</ControlsContainer>
